import { forwardRef, useCallback } from 'react';
import { cn } from '../../utils/cn';

/**
 * `type="number"` input için ortak focus davranışı:
 *   - Değer 0 ise → input tamamen TEMİZLENİR (controlled state de boşa çekilir);
 *     kullanıcı doğrudan rakam yazmaya başlar, "0"ı silmeye gerek kalmaz.
 *   - Mevcut bir değer varsa (örn. 100, 15000) → cursor tıkladığı yerde
 *     kalır, kullanıcı kısmi düzenleme yapabilir.
 *
 * 2026-05-15: Eski davranış `e.target.select()` idi — metni seçili yapıyordu
 * ama kullanıcı escape ederse 0 görünür kalıyordu. Yeni davranış native value
 * setter + sentetik input event ile React state'i de temizler.
 */
function shouldClearOnFocus(value) {
  return Number(value) === 0;
}

/**
 * Native HTMLInputElement.value setter — React'in controlled input
 * tracking'iyle uyumlu şekilde DOM değerini değiştirir. Ardından `input`
 * event dispatch edilince React `onChange` handler'ı tetiklenir, çağıran
 * component'in state'i de boşa çekilir. Standart pattern.
 */
function clearInputControlled(inputEl) {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  )?.set;
  if (nativeSetter) {
    nativeSetter.call(inputEl, '');
    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
  } else {
    // Fallback — eski tarayıcı ya da prototip yok; doğrudan select.
    inputEl.select();
  }
}

/**
 * Input + Label + Hint/Error — form için tekil component.
 *
 * type="number" için merkezi UX kuralları:
 *   1. Focus alındığında değer 0 ise tüm metin seçilir (placeholder davranışı).
 *   2. Negatif değer girişi engellenir:
 *      - Default `min="0"` set edilir (caller `min` geçmediyse).
 *      - "-" tuşu klavyeden engellenir (onKeyDown preventDefault).
 *      - Yapıştırılan metinde "-" varsa paste iptal edilir.
 *   3. `allowNegative` prop'u ile bu davranış kapatılabilir (ileride
 *      manuel skor düzeltme gibi negatif değer gerektiren durumlar için).
 *
 * Tüm bu davranışlar caller'ın kendi onFocus / onKeyDown / onPaste handler'ını
 * BOZMAZ; varsa sırayla çağrılır (composability).
 */
const Input = forwardRef(function Input(
  {
    label,
    hint,
    error,
    className,
    required,
    id,
    type,
    onFocus,
    onKeyDown,
    onPaste,
    min,
    allowNegative = false,
    ...rest
  },
  ref,
) {
  const autoId = id || `in-${Math.random().toString(36).slice(2, 8)}`;
  const isNumberInput = type === 'number';
  const blockNegative = isNumberInput && !allowNegative;
  // Default `min` — caller belirtmediyse ve negatif yasaksa 0 koy.
  const effectiveMin = min !== undefined ? min : (blockNegative ? 0 : undefined);

  const handleFocus = useCallback(
    (e) => {
      if (isNumberInput && shouldClearOnFocus(e.target.value)) {
        // Sentetik input event dispatch ederek controlled state'i temizle.
        // Kullanıcı escape etse bile input boş kalır; yeni değer için
        // doğrudan rakam yazılabilir.
        clearInputControlled(e.target);
      }
      if (onFocus) onFocus(e);
    },
    [isNumberInput, onFocus],
  );

  const handleKeyDown = useCallback(
    (e) => {
      // "-" tuşu negatif sayı girişini başlatır → engelle.
      // "e", "E" üstel notasyon (1e5) anlamlı olabilir, dokunmuyoruz.
      if (blockNegative && (e.key === '-' || e.key === 'Subtract')) {
        e.preventDefault();
      }
      if (onKeyDown) onKeyDown(e);
    },
    [blockNegative, onKeyDown],
  );

  const handlePaste = useCallback(
    (e) => {
      if (blockNegative) {
        const pasted = (e.clipboardData || window.clipboardData)?.getData?.('text') || '';
        if (pasted.includes('-')) {
          e.preventDefault();
          return;
        }
      }
      if (onPaste) onPaste(e);
    },
    [blockNegative, onPaste],
  );

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={autoId} className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
          {required && <span className="text-danger-600 ml-0.5">*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={autoId}
        type={type}
        min={effectiveMin}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cn(
          'w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 outline-none transition-colors focus:ring-2 focus:ring-primary-500/20',
          error
            ? 'border-danger-400 focus:border-danger-500'
            : 'border-neutral-200 focus:border-primary-500',
          className,
        )}
        {...rest}
      />
      {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
      {!error && hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
});

export default Input;

export const Textarea = forwardRef(function Textarea(
  { label, hint, error, className, required, id, rows = 3, ...rest },
  ref,
) {
  const autoId = id || `ta-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={autoId} className="block text-sm font-medium text-neutral-700 mb-1">
          {label}
          {required && <span className="text-danger-600 ml-0.5">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={autoId}
        rows={rows}
        className={cn(
          'w-full rounded-lg border bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 outline-none transition-colors focus:ring-2 focus:ring-primary-500/20',
          error
            ? 'border-danger-400 focus:border-danger-500'
            : 'border-neutral-200 focus:border-primary-500',
          className,
        )}
        {...rest}
      />
      {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
      {!error && hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
    </div>
  );
});
